import React, { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import Button from '../ui/Button';
import Pill from '../ui/Pill';
import StatusBanner from '../ui/StatusBanner';
import './PlatformIntegrationsTab.css';

/**
 * Platform Admin → Integrations tab.
 *
 * VizionOps is a managed-service SaaS: integration *configuration*
 * (credentials, webhook secret, intake behavior) is administered by
 * Platform Admin per company. The Client Portal consumes integration
 * output only (Callout History) and never sees secrets.
 *
 * SMS intake supports TWO providers — EZ Texting and Twilio. The
 * company's selected provider is stored server-side; the matching
 * credential form is shown for the selected provider only. Both
 * providers' secrets (EZ Texting API password, Twilio auth token) are
 * redacted by the GET endpoints to boolean `has…` flags and never
 * reach the client.
 *
 * Mounts inside CompanyManagementModal next to PlatformBillingTab and
 * DigestConfigPanel. Targets platform-scoped routes
 *   /api/platform/companies/:companyId/integrations/eztexting
 *   /api/platform/companies/:companyId/integrations/twilio
 *   /api/platform/companies/:companyId/integrations/sms-provider
 * all gated by canAccessPlatformWorkspace and reading the explicit
 * :companyId param.
 */

const EZ_EMPTY = {
  apiUsername: '',
  apiPassword: '',
  attendancePhoneNumber: '',
  webhookSecret: '',
  autoReplyEnabled: true,
  autoReplyMatchedTemplate: '',
  autoReplyReviewTemplate: '',
  autoReplyUnmatchedTemplate: '',
  autoCreateSick: true,
  autoCreateAbsent: true,
  autoCreateLate: true,
  ptoMode: 'review_only',
  hasApiPassword: false,
  webhookLastReceivedAt: null,
  webhookLastStatus: 'not_configured',
  webhookLastError: '',
  webhookUrl: ''
};

const TWILIO_EMPTY = {
  accountSid: '',
  authToken: '',
  phoneNumber: '',
  hasAuthToken: false,
  webhookLastReceivedAt: null,
  webhookLastStatus: 'not_configured',
  webhookLastError: '',
  webhookUrl: ''
};

function statusTone(connected, lastStatus) {
  if (!connected) return 'neutral';
  if (lastStatus === 'error') return 'danger';
  if (lastStatus === 'ok' || lastStatus === 'received') return 'success';
  return 'info';
}

function PlatformIntegrationsTab({ companyId, companyName }) {
  const [provider, setProvider] = useState('ez_texting');
  const [ez, setEz] = useState(EZ_EMPTY);
  const [twilio, setTwilio] = useState(TWILIO_EMPTY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [saveBusy, setSaveBusy] = useState(false);
  const [providerBusy, setProviderBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const ezUrl = `/api/platform/companies/${companyId}/integrations/eztexting`;
  const twilioUrl = `/api/platform/companies/${companyId}/integrations/twilio`;
  const providerUrl = `/api/platform/companies/${companyId}/integrations/sms-provider`;

  const refetch = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    setError('');
    try {
      const [ezSettings, twilioSettings] = await Promise.all([
        apiRequest(ezUrl),
        apiRequest(twilioUrl)
      ]);
      setEz({ ...EZ_EMPTY, ...ezSettings, apiPassword: '' });
      setTwilio({ ...TWILIO_EMPTY, ...twilioSettings, authToken: '' });
      setProvider(twilioSettings?.selectedProvider || 'ez_texting');
    } catch (err) {
      setError(err?.message || 'Unable to load integration settings.');
    } finally {
      setIsLoading(false);
    }
  }, [ezUrl, twilioUrl, companyId]);

  useEffect(() => { refetch(); }, [refetch]);

  const setEzField = useCallback((field, value) => {
    setEz((current) => ({ ...current, [field]: value }));
  }, []);
  const setTwilioField = useCallback((field, value) => {
    setTwilio((current) => ({ ...current, [field]: value }));
  }, []);

  const handleProviderChange = useCallback(async (nextProvider) => {
    if (nextProvider === provider) return;
    setProvider(nextProvider);
    setProviderBusy(true);
    setError('');
    setInfo('');
    try {
      await apiRequest(providerUrl, {
        method: 'PUT',
        body: JSON.stringify({ provider: nextProvider })
      });
      setInfo(`SMS provider set to ${nextProvider === 'twilio' ? 'Twilio' : 'EZ Texting'}.`);
    } catch (err) {
      setError(err?.message || 'Unable to change SMS provider.');
    } finally {
      setProviderBusy(false);
    }
  }, [provider, providerUrl]);

  const handleSaveEz = useCallback(async (event) => {
    event?.preventDefault?.();
    setSaveBusy(true);
    setError('');
    setInfo('');
    try {
      const settings = await apiRequest(ezUrl, {
        method: 'PUT',
        body: JSON.stringify({
          apiUsername: ez.apiUsername,
          apiPassword: ez.apiPassword,
          attendancePhoneNumber: ez.attendancePhoneNumber,
          webhookSecret: ez.webhookSecret,
          autoReplyEnabled: ez.autoReplyEnabled,
          autoReplyMatchedTemplate: ez.autoReplyMatchedTemplate,
          autoReplyReviewTemplate: ez.autoReplyReviewTemplate,
          autoReplyUnmatchedTemplate: ez.autoReplyUnmatchedTemplate,
          autoCreateSick: ez.autoCreateSick,
          autoCreateAbsent: ez.autoCreateAbsent,
          autoCreateLate: ez.autoCreateLate,
          ptoMode: ez.ptoMode
        })
      });
      setEz({ ...EZ_EMPTY, ...settings, apiPassword: '' });
      setInfo('Saved EZ Texting integration settings.');
    } catch (err) {
      setError(err?.message || 'Unable to save integration settings.');
    } finally {
      setSaveBusy(false);
    }
  }, [ezUrl, ez]);

  const handleSaveTwilio = useCallback(async (event) => {
    event?.preventDefault?.();
    setSaveBusy(true);
    setError('');
    setInfo('');
    try {
      const settings = await apiRequest(twilioUrl, {
        method: 'PUT',
        body: JSON.stringify({
          accountSid: twilio.accountSid,
          authToken: twilio.authToken,
          phoneNumber: twilio.phoneNumber
        })
      });
      setTwilio({ ...TWILIO_EMPTY, ...settings, authToken: '' });
      setInfo('Saved Twilio integration settings.');
    } catch (err) {
      setError(err?.message || 'Unable to save integration settings.');
    } finally {
      setSaveBusy(false);
    }
  }, [twilioUrl, twilio]);

  const activeWebhookUrl = provider === 'twilio' ? twilio.webhookUrl : ez.webhookUrl;
  const handleCopyWebhook = useCallback(async () => {
    if (!activeWebhookUrl) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(activeWebhookUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    } catch (_err) {
      // Clipboard can fail silently in non-secure contexts; the URL is
      // still visible to copy manually.
    }
  }, [activeWebhookUrl]);

  const isTwilio = provider === 'twilio';
  const connected = isTwilio ? Boolean(twilio.hasAuthToken) : Boolean(ez.hasApiPassword);
  const lastStatus = isTwilio ? twilio.webhookLastStatus : ez.webhookLastStatus;
  const lastReceivedAt = isTwilio ? twilio.webhookLastReceivedAt : ez.webhookLastReceivedAt;
  const lastError = isTwilio ? twilio.webhookLastError : ez.webhookLastError;
  const providerLabel = isTwilio ? 'Twilio' : 'EZ Texting';

  return (
    <section
      className="director-card platform-integrations-tab"
      data-testid="platform-integrations-tab"
    >
      <div className="role-section-header">
        <h2>Integrations</h2>
        <Pill
          variant={statusTone(connected, lastStatus)}
          data-testid="platform-integrations-status-pill"
        >
          {connected ? 'Connected' : 'Not connected'}
        </Pill>
      </div>

      {error ? <StatusBanner kind="error" message={error} live /> : null}
      {info ? <StatusBanner kind="success" message={info} live /> : null}

      {isLoading ? (
        <div className="platform-integrations-loading">Loading integration settings…</div>
      ) : (
        <>
          <label className="platform-integrations-provider">
            SMS provider
            <select
              value={provider}
              onChange={(e) => handleProviderChange(e.target.value)}
              disabled={providerBusy}
              data-testid="platform-integrations-provider-select"
            >
              <option value="ez_texting">EZ Texting</option>
              <option value="twilio">Twilio</option>
            </select>
          </label>

          <dl className="platform-integrations-status">
            <div data-testid="platform-integrations-connection">
              <dt>{providerLabel}</dt>
              <dd>{connected ? 'Connected' : 'Not connected'}</dd>
            </div>
            <div data-testid="platform-integrations-last-webhook">
              <dt>Last webhook received</dt>
              <dd>
                {lastReceivedAt
                  ? new Date(lastReceivedAt).toLocaleString()
                  : '—'}
              </dd>
            </div>
            <div data-testid="platform-integrations-webhook-state">
              <dt>Webhook status</dt>
              <dd>{lastStatus || 'not_configured'}</dd>
            </div>
            {lastError ? (
              <div
                className="platform-integrations-status-error"
                data-testid="platform-integrations-last-error"
              >
                <dt>Last error</dt>
                <dd>{lastError}</dd>
              </div>
            ) : null}
          </dl>

          <div className="platform-integrations-webhook-url">
            <span className="platform-integrations-webhook-url-label">
              {isTwilio ? 'Twilio webhook URL' : 'Callback URL'}
            </span>
            <code data-testid="platform-integrations-webhook-url">
              {activeWebhookUrl || 'Set API_BASE_URL to expose a full webhook URL.'}
            </code>
            {activeWebhookUrl ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCopyWebhook}
                data-testid="platform-integrations-copy-webhook"
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            ) : null}
          </div>

          {isTwilio ? (
            <form
              className="platform-integrations-form"
              onSubmit={handleSaveTwilio}
              data-testid="platform-integrations-twilio-form"
            >
              <label>
                Account SID
                <input
                  value={twilio.accountSid}
                  onChange={(e) => setTwilioField('accountSid', e.target.value)}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  data-testid="platform-integrations-twilio-account-sid"
                />
              </label>
              <label>
                Auth token
                <input
                  type="password"
                  value={twilio.authToken}
                  onChange={(e) => setTwilioField('authToken', e.target.value)}
                  placeholder={twilio.hasAuthToken ? 'Stored securely. Enter a new value to replace.' : 'Twilio auth token'}
                  data-testid="platform-integrations-twilio-auth-token"
                />
              </label>
              <label>
                Twilio phone number
                <input
                  value={twilio.phoneNumber}
                  onChange={(e) => setTwilioField('phoneNumber', e.target.value)}
                  placeholder="+1 555 555 5555"
                  data-testid="platform-integrations-twilio-phone"
                />
              </label>

              <div className="platform-integrations-actions platform-integrations-span">
                <Button
                  size="sm"
                  type="submit"
                  disabled={saveBusy}
                  data-testid="platform-integrations-twilio-save"
                >
                  {saveBusy ? 'Saving…' : 'Save Twilio integration'}
                </Button>
                {companyName ? (
                  <span className="platform-integrations-tenant-label">{companyName}</span>
                ) : null}
              </div>
            </form>
          ) : (
            <form
              className="platform-integrations-form"
              onSubmit={handleSaveEz}
              data-testid="platform-integrations-eztexting-form"
            >
              <label>
                API username
                <input
                  value={ez.apiUsername}
                  onChange={(e) => setEzField('apiUsername', e.target.value)}
                  placeholder="EZ Texting API username"
                  data-testid="platform-integrations-api-username"
                />
              </label>
              <label>
                API password
                <input
                  type="password"
                  value={ez.apiPassword}
                  onChange={(e) => setEzField('apiPassword', e.target.value)}
                  placeholder={ez.hasApiPassword ? 'Stored securely. Enter a new value to replace.' : 'EZ Texting API password'}
                  data-testid="platform-integrations-api-password"
                />
              </label>
              <label>
                Attendance phone number
                <input
                  value={ez.attendancePhoneNumber}
                  onChange={(e) => setEzField('attendancePhoneNumber', e.target.value)}
                  placeholder="+1 555 555 5555"
                  data-testid="platform-integrations-phone"
                />
              </label>
              <label>
                Webhook secret
                <input
                  type="password"
                  value={ez.webhookSecret}
                  onChange={(e) => setEzField('webhookSecret', e.target.value)}
                  placeholder="Optional HMAC secret for webhook verification"
                  data-testid="platform-integrations-webhook-secret"
                />
              </label>

              <label className="platform-integrations-toggle platform-integrations-span">
                <input
                  type="checkbox"
                  checked={ez.autoReplyEnabled}
                  onChange={(e) => setEzField('autoReplyEnabled', e.target.checked)}
                  data-testid="platform-integrations-auto-reply"
                />
                <span>Send confirmation replies</span>
              </label>

              <div className="platform-integrations-toggle-row platform-integrations-span">
                <label className="platform-integrations-toggle">
                  <input
                    type="checkbox"
                    checked={ez.autoCreateSick}
                    onChange={(e) => setEzField('autoCreateSick', e.target.checked)}
                    data-testid="platform-integrations-auto-sick"
                  />
                  <span>Auto-create Sick Call</span>
                </label>
                <label className="platform-integrations-toggle">
                  <input
                    type="checkbox"
                    checked={ez.autoCreateAbsent}
                    onChange={(e) => setEzField('autoCreateAbsent', e.target.checked)}
                    data-testid="platform-integrations-auto-absent"
                  />
                  <span>Auto-create Other Absence</span>
                </label>
                <label className="platform-integrations-toggle">
                  <input
                    type="checkbox"
                    checked={ez.autoCreateLate}
                    onChange={(e) => setEzField('autoCreateLate', e.target.checked)}
                    data-testid="platform-integrations-auto-late"
                  />
                  <span>Auto-create Late Arrival</span>
                </label>
              </div>

              <label>
                PTO intake mode
                <select
                  value={ez.ptoMode}
                  onChange={(e) => setEzField('ptoMode', e.target.value)}
                  data-testid="platform-integrations-pto-mode"
                >
                  <option value="review_only">Review only</option>
                  <option value="ignore">Ignore PTO texts</option>
                </select>
              </label>

              <div className="platform-integrations-actions platform-integrations-span">
                <Button
                  size="sm"
                  type="submit"
                  disabled={saveBusy}
                  data-testid="platform-integrations-save"
                >
                  {saveBusy ? 'Saving…' : 'Save integration'}
                </Button>
                {companyName ? (
                  <span className="platform-integrations-tenant-label">{companyName}</span>
                ) : null}
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
}

export default PlatformIntegrationsTab;
